import type { Embedding } from '@shared/utils/search';
import { cosineSimilarity, useLatestEmbedding } from '@shared/utils/search';
import { createContext } from '@shared/utils/createContext';
import { useDebounce } from '@shared/utils/hooks/useDebounce';
import { useManagedSearchParam } from '@shared/hooks/useManagedSearchParam';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchTermTelemetry } from './useSearchTermTelemetry';
import { usePrecomputedEmbeddings } from '@shared/utils/search/usePrecomputedEmbeddings';
import { SIMILARITY_THRESHOLD } from '@shared/utils/search/scoreAndFilterByEmbedding';

const V2_SEARCH_QUERY_PARAM = 'q';

type SearchContextType = Readonly<{
  activeTerm: string;
  activeTermEmbedding: Embedding | undefined;
  setTerm: (term: string) => void;
  wantsFree: boolean;
}>;

const [SearchContextProvider, useSearchContext] = createContext<SearchContextType>({
  name: 'SearchContext',
  initialValue: {
    activeTerm: '',
    activeTermEmbedding: undefined,
    setTerm: (_term: string) => {},
    wantsFree: false,
  },
});

export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [initialQ, setQ] = useManagedSearchParam(V2_SEARCH_QUERY_PARAM);
  const [term, setTerm] = useState<string>(initialQ ?? '');
  const debouncedTerm = useDebounce(term, 200);

  // For Chinese and similar, we relax the length requirement.
  // Single character like "红" would be a common search term.
  // Want to use modern /^\p{Script=Han}/u, but it requires es6 ts(1501)
  const isHanTerm = /^[\u4E00-\u9FFF\u3400-\u4DBF\uF900-\uFAFF]/.test(debouncedTerm); // Only need to check first char.
  const minLength = isHanTerm ? 1 : 3;
  const activeTerm = debouncedTerm.length >= minLength ? debouncedTerm : '';

  const activeTermEmbedding = useLatestEmbedding(activeTerm);
  useSearchTermTelemetry(activeTerm);

  const { embeddingMap } = usePrecomputedEmbeddings();
  const embeddingFree = embeddingMap?.get('free');
  const wantsFree = embeddingFree && activeTermEmbedding ? cosineSimilarity(activeTermEmbedding, embeddingFree) > SIMILARITY_THRESHOLD : false; // Assumed either `embeddingFree` or `activeTermEmbedding` are still loading

  useEffect(() => {
    setQ(activeTerm || null);
  }, [activeTerm, setQ]);

  const contextValue = useMemo(() => {
    return {
      activeTerm,
      activeTermEmbedding,
      setTerm,
      wantsFree,
    };
    // We skip `activeTerm` in deps because we want to wait for `activeTermEmbedding` before broadcasting changes.
    // This way `activeTerm` and `activeTermEmbedding` stay in sync. Otherwise `activeTerm` would change first.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTermEmbedding, setTerm, wantsFree]);
  return <SearchContextProvider value={contextValue}>{children}</SearchContextProvider>;
};

export { useSearchContext };
